"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { useContactInfoOperations } from "@/hooks/useContactInfoOperations"
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, AlertCircle } from "lucide-react"
import type { ContactInfoResDTO, ContactInfoUpdateReqDTO } from "@/types/api"

interface ContactInfoModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (contactInfo: ContactInfoResDTO) => void
  initialData?: ContactInfoResDTO
}

export function ContactInfoModal({ isOpen, onClose, onSuccess, initialData }: ContactInfoModalProps) {
  const [formData, setFormData] = useState<ContactInfoUpdateReqDTO>({
    displayPhone: initialData?.displayPhone || "+34910052212",
    displayEmail: initialData?.displayEmail || "info@raimerentals.com",
  })
  const { loading, error, updateContactInfo, clearError } = useContactInfoOperations()

  // Use the body scroll lock hook to ensure proper cleanup
  useBodyScrollLock(isOpen)

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        displayPhone: "",
        displayEmail: "",
      })
      clearError()
      return
    }

    // Set initial data when opening with fallbacks
    setFormData({
      displayPhone: initialData?.displayPhone || "+34910052212",
      displayEmail: initialData?.displayEmail || "info@raimerentals.com",
    })
    clearError()
  }, [isOpen, initialData, clearError])

  const handleInputChange = useCallback((field: keyof ContactInfoUpdateReqDTO, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (error) clearError()
  }, [error, clearError])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      // Safely get form values with fallbacks
      const email = formData?.displayEmail || ""
      const phone = formData?.displayPhone || ""

      if (!email.trim() || !phone.trim()) {
        // We'll use the error from the hook, but we need to set a custom error here
        // Since the hook doesn't handle form validation errors
        return
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email.trim())) {
        return
      }

      const result = await updateContactInfo({
        displayPhone: phone.trim(),
        displayEmail: email.trim(),
      })

      if (result) {
        onSuccess(result)
        onClose()
      }
    },
    [formData, updateContactInfo, onSuccess, onClose],
  )

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose()
    }
  }, [loading, onClose])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !loading) {
        onClose()
      }
    },
    [loading, onClose],
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Información de Contacto</DialogTitle>
          <DialogDescription>
            Actualiza la información de contacto que se muestra en el footer del sitio web.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="displayEmail">
              Email de Contacto <span className="text-red-500">*</span>
            </Label>
            <Input
              id="displayEmail"
              type="email"
              value={formData?.displayEmail || ""}
              onChange={(e) => handleInputChange("displayEmail", e.target.value)}
              placeholder="info@raimerentals.com"
              disabled={loading}
              required
              className="w-full"
            />
          </div>

          {/* Phone Input */}
          <div className="space-y-2">
            <Label htmlFor="displayPhone">
              Teléfono de Contacto <span className="text-red-500">*</span>
            </Label>
            <Input
              id="displayPhone"
              type="tel"
              value={formData?.displayPhone || ""}
              onChange={(e) => handleInputChange("displayPhone", e.target.value)}
              placeholder="+34910052212"
              disabled={loading}
              required
              className="w-full"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !(formData?.displayEmail || "").trim() || !(formData?.displayPhone || "").trim()}
              className="bg-raime-golden-brown hover:bg-raime-golden-brown/90 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Actualizando...
                </>
              ) : (
                "Guardar Cambios"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}